import { useState, useEffect } from 'react';
import { Users, MessageSquare, Bell, Award, Check, RotateCcw, Plus, Coins } from 'lucide-react';
import nhost from '../nhost';
import ImageUpload from './ImageUpload';
import { POINTS } from '../points';

const PENDING_QUERY = `query { profiles(where: { approved: { _eq: false } }) { id display_name } }`;
const ALL_QUERY = `query { profiles(order_by: { display_name: asc }) { id display_name approved is_admin } }`;
const FEEDBACK_QUERY = `query { feedback(order_by: { created_at: desc }) { id type message read resolved in_progress created_at profile { display_name } } }`;
const BADGES_QUERY = `query { badges(order_by: [{is_auto: desc}, {name: asc}]) { id name description image_url is_auto user_badges { user_id } } }`;

const TYPE_META = {
  bug:        { label: 'Problem',  color: '#a32d2d', bg: '#fcebeb' },
  feedback:   { label: 'Feedback', color: 'var(--wine)', bg: 'var(--wine-light)' },
  suggestion: { label: 'Förslag',  color: '#A07830', bg: 'var(--gold-light)' },
};

const ACTIVITY_LABELS = {
  rate_wine: 'Betygsatt vin',
  comment_wine: 'Kommenterat vin',
  session_comment: 'Provningskommentar',
  upload_photo: 'Uppladdad bild',
  wine_interest: 'Markerat intresse för vin',
  profile_avatar: 'Profilbild uppladdad',
  profile_phone: 'Telefonnummer sparat',
  host_session: 'Hållit provning',
  attend_session: 'Deltagit i provning',
  winebox_rsvp: 'Accepterat vinlåda',
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '8px 4px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
        border: 'none',
        background: active ? 'var(--wine)' : 'rgba(60,20,30,.06)',
        color: active ? '#fff' : 'var(--muted)',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

const sectionTitle = {
  fontSize: 10, fontWeight: 600, letterSpacing: 1.5,
  textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10,
};

export default function Admin({ profile, onClose }) {
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [tab, setTab] = useState('users');
  const [inboxTab, setInboxTab] = useState('active');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [sendingPush, setSendingPush] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushSubCount, setPushSubCount] = useState(null);
  const [pushUserCount, setPushUserCount] = useState(null);
  const [pushSubUserIds, setPushSubUserIds] = useState(new Set());
  const [pushTargetUserIds, setPushTargetUserIds] = useState([]);
  const [badges, setBadges] = useState([]);
  const [showCreateBadge, setShowCreateBadge] = useState(false);
  const [newBadgeName, setNewBadgeName] = useState('');
  const [newBadgeDesc, setNewBadgeDesc] = useState('');
  const [newBadgeImageUrl, setNewBadgeImageUrl] = useState(null);
  const [savingBadge, setSavingBadge] = useState(false);
  const [awardingBadge, setAwardingBadge] = useState(null);
  const [awardToUsers, setAwardToUsers] = useState([]);
  const [sendingAward, setSendingAward] = useState(false);
  const [awardResult, setAwardResult] = useState(null);
  const [editingBadgeImage, setEditingBadgeImage] = useState(null);
  const [pointsUserId, setPointsUserId] = useState('');
  const [pointsActivity, setPointsActivity] = useState(Object.keys(POINTS)[0]);
  const [pointsReferenceId, setPointsReferenceId] = useState('');
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsDirection, setPointsDirection] = useState('add');
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [pointsResult, setPointsResult] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [p, a, f, ps, bg] = await Promise.all([
      nhost.graphql.request(PENDING_QUERY),
      nhost.graphql.request(ALL_QUERY),
      nhost.graphql.request(FEEDBACK_QUERY),
      fetch('/api/push?action=send').then(r => r.json()),
      nhost.graphql.request(BADGES_QUERY),
    ]);
    if (p.data) setPending(p.data.profiles);
    if (a.data) setAll(a.data.profiles);
    if (ps.ok) { setPushSubCount(ps.devices); setPushUserCount(ps.users); setPushSubUserIds(new Set(ps.subscribedUserIds || [])); }
    if (f.data) setFeedback(f.data.feedback);
    if (bg.data) setBadges(bg.data.badges);
  }

  async function createBadge() {
    if (!newBadgeName.trim()) return;
    setSavingBadge(true);
    await fetch('/api/badges', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create', name: newBadgeName.trim(), description: newBadgeDesc.trim() || null, image_url: newBadgeImageUrl || null, created_by: profile.id }),
    });
    setNewBadgeName(''); setNewBadgeDesc(''); setNewBadgeImageUrl(null);
    setShowCreateBadge(false); setSavingBadge(false);
    fetchAll();
  }

  async function awardBadge() {
    if (!awardingBadge || awardToUsers.length === 0) return;
    setSendingAward(true);
    const r = await fetch('/api/badges', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'award', badgeId: awardingBadge.id, userIds: awardToUsers, awardedBy: profile.id }),
    });
    const json = await r.json();
    setAwardResult(`Delat ut till ${json.awarded} ${json.awarded === 1 ? 'medlem' : 'medlemmar'}.`);
    setSendingAward(false); setAwardToUsers([]);
    fetchAll();
  }

  async function updateBadgeImage(badgeId, imageUrl) {
    await fetch('/api/badges', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ badgeId, image_url: imageUrl }),
    });
    setEditingBadgeImage(null);
    fetchAll();
  }

  async function adjustPoints() {
    const amount = Math.abs(Number(pointsAmount));
    if (!pointsUserId || !pointsActivity || !pointsReferenceId.trim() || !amount) return;
    const finalPoints = pointsDirection === 'remove' ? -amount : amount;
    const user = all.find(u => u.id === pointsUserId);
    const activityLabel = ACTIVITY_LABELS[pointsActivity] || pointsActivity;
    const confirmMsg = pointsDirection === 'remove'
      ? `Dra av ${amount}p (${activityLabel}) från ${user?.display_name}?`
      : `Lägg till ${amount}p (${activityLabel}) till ${user?.display_name}?`;
    if (!window.confirm(confirmMsg)) return;

    setAdjustingPoints(true);
    setPointsResult(null);

    const { data } = await nhost.graphql.request(`
      mutation AdjustPoints($obj: user_points_insert_input!) {
        insert_user_points_one(
          object: $obj,
          on_conflict: { constraint: user_points_user_id_activity_reference_id_key, update_columns: [] }
        ) { id }
      }
    `, {
      obj: {
        user_id: pointsUserId,
        activity: pointsActivity,
        points: finalPoints,
        reference_id: pointsReferenceId.trim(),
      }
    });

    if (!data?.insert_user_points_one?.id) {
      setPointsResult({ error: true, message: 'Det finns redan en poängpost med den aktiviteten och referens-ID:t för den här användaren. Ingen ändring gjordes.' });
      setAdjustingPoints(false);
      return;
    }

    const { data: totalData } = await nhost.graphql.request(
      `query($uid: uuid!) { user_points(where: { user_id: { _eq: $uid } }) { points } }`,
      { uid: pointsUserId }
    );
    const total = totalData?.user_points?.reduce((sum, p) => sum + p.points, 0) ?? 0;
    setPointsResult({ error: false, message: `Klart. ${user?.display_name} har nu ${total}p totalt.` });
    setPointsReferenceId('');
    setPointsAmount('');
    setAdjustingPoints(false);
  }

  async function approve(id) {
    await fetch('/api/approve-user', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: id }) });
    fetchAll();
  }

  async function makeAdmin(id) {
    await fetch('/api/approve-user', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: id, isAdmin: true }) });
    fetchAll();
  }

  async function markRead(id) {
    await nhost.graphql.request(`mutation { update_feedback_by_pk(pk_columns: { id: "${id}" }, _set: { read: true }) { id } }`);
    setFeedback(prev => prev.map(f => f.id === id ? { ...f, read: true } : f));
  }

  async function markInProgress(id, inProgress) {
    await nhost.graphql.request(`mutation { update_feedback_by_pk(pk_columns: { id: "${id}" }, _set: { in_progress: ${inProgress}, read: true }) { id } }`);
    setFeedback(prev => prev.map(f => f.id === id ? { ...f, in_progress: inProgress, read: true } : f));
  }

  async function markResolved(id, resolved) {
    await nhost.graphql.request(`mutation { update_feedback_by_pk(pk_columns: { id: "${id}" }, _set: { resolved: ${resolved}, in_progress: false, read: true }) { id } }`);
    setFeedback(prev => prev.map(f => f.id === id ? { ...f, resolved, in_progress: false, read: true } : f));
  }

  async function sendPush() {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    setSendingPush(true); setPushResult(null);
    try {
      const r = await fetch('/api/push?action=send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: pushTitle.trim(), body: pushBody.trim(), url: '/', data: { type: 'admin_message', message: pushBody.trim() }, ...(pushTargetUserIds.length > 0 ? { userIds: pushTargetUserIds } : {}) }),
      });
      const json = await r.json();
      setPushResult(`Skickat till ${json.sent} enhet${json.sent !== 1 ? 'er' : ''}.`);
      setPushTitle(''); setPushBody('');
    } catch { setPushResult('Något gick fel.'); }
    setSendingPush(false);
  }

  const unreadCount = feedback.filter(f => !f.read && !f.resolved).length;
  const activeFeedback = feedback.filter(f => !f.resolved);
  const resolvedFeedback = feedback.filter(f => f.resolved);

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Admin</div>

        {/* Flikrad */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          <TabBtn active={tab === 'users'} onClick={() => setTab('users')}>
            <Users size={13} strokeWidth={1.8} />
            Medlemmar{pending.length > 0 ? ` (${pending.length})` : ''}
          </TabBtn>
          <TabBtn active={tab === 'feedback'} onClick={() => setTab('feedback')}>
            <MessageSquare size={13} strokeWidth={1.8} />
            Inkorg
            {unreadCount > 0 && <span style={{ background: '#fff', color: 'var(--wine)', borderRadius: 10, padding: '0 5px', fontSize: 10, fontWeight: 700, marginLeft: 2 }}>{unreadCount}</span>}
          </TabBtn>
          <TabBtn active={tab === 'push'} onClick={() => setTab('push')}>
            <Bell size={13} strokeWidth={1.8} />
            Notis
          </TabBtn>
          <TabBtn active={tab === 'badges'} onClick={() => setTab('badges')}>
            <Award size={13} strokeWidth={1.8} />
            Badges
          </TabBtn>
          <TabBtn active={tab === 'points'} onClick={() => setTab('points')}>
            <Coins size={13} strokeWidth={1.8} />
            Poäng
          </TabBtn>
        </div>

        {/* === ANVÄNDARE === */}
        {tab === 'users' && (
          <>
            {pending.length > 0 && (
              <>
                <div style={{ ...sectionTitle, color: '#a32d2d' }}>Väntar på godkännande ({pending.length})</div>
                {pending.map((u, i) => (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', ...(i > 0 ? divider : {}) }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{u.display_name}</div>
                    <button onClick={() => approve(u.id)} style={{ background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Godkänn
                    </button>
                  </div>
                ))}
                <div style={{ height: 16 }} />
              </>
            )}
            <div style={sectionTitle}>Alla medlemmar</div>
            <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 12, overflow: 'hidden' }}>
              {all.map((u, i) => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', ...(i > 0 ? divider : {}) }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{u.display_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {u.approved
                        ? <><Check size={10} strokeWidth={2.5} color="var(--teal)" /> Godkänd</>
                        : <span style={{ color: '#B45309' }}>Väntar</span>
                      }
                      {u.is_admin && <span style={{ color: 'var(--wine)', fontWeight: 600 }}> · Admin</span>}
                    </div>
                  </div>
                  {u.approved && !u.is_admin && (
                    <button onClick={() => makeAdmin(u.id)} style={{ background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>
                      Gör till admin
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* === INKORG === */}
        {tab === 'feedback' && (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              <button onClick={() => setInboxTab('active')} style={{ flex: 1, padding: '7px', borderRadius: 20, cursor: 'pointer', fontSize: 12, border: 'none', background: inboxTab === 'active' ? 'var(--wine)' : 'rgba(60,20,30,.06)', color: inboxTab === 'active' ? '#fff' : 'var(--muted)', fontWeight: inboxTab === 'active' ? 600 : 400, fontFamily: 'inherit' }}>
                Inkorg {activeFeedback.length > 0 ? `(${activeFeedback.length})` : ''}
              </button>
              <button onClick={() => setInboxTab('resolved')} style={{ flex: 1, padding: '7px', borderRadius: 20, cursor: 'pointer', fontSize: 12, border: 'none', background: inboxTab === 'resolved' ? 'var(--teal)' : 'rgba(60,20,30,.06)', color: inboxTab === 'resolved' ? '#fff' : 'var(--muted)', fontWeight: inboxTab === 'resolved' ? 600 : 400, fontFamily: 'inherit' }}>
                Avklarade {resolvedFeedback.length > 0 ? `(${resolvedFeedback.length})` : ''}
              </button>
            </div>

            {inboxTab === 'active' && (
              <>
                {activeFeedback.length === 0 && <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--muted)', fontSize: 13 }}>Inga aktiva ärenden.</div>}
                {activeFeedback.map((f, i) => {
                  const meta = TYPE_META[f.type] || TYPE_META.feedback;
                  return (
                    <div key={f.id} style={{ padding: '12px 0', ...(i > 0 ? divider : {}) }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, background: meta.bg, color: meta.color, borderRadius: 20, padding: '2px 9px', fontWeight: 600 }}>{meta.label}</span>
                          {f.in_progress && (
                            <span style={{ fontSize: 11, background: '#FEF3C7', color: '#B45309', borderRadius: 10, padding: '2px 8px', fontWeight: 500 }}>
                              Under hantering
                            </span>
                          )}
                          {!f.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--wine)', flexShrink: 0 }} />}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{f.profile?.display_name} · {new Date(f.created_at).toLocaleDateString('sv-SE')}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>{f.message}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {!f.read && (
                          <button onClick={() => markRead(f.id)} style={{ fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                            Markera som läst
                          </button>
                        )}
                        <button
                          onClick={() => markInProgress(f.id, !f.in_progress)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: f.in_progress ? '#B45309' : 'var(--muted)', background: f.in_progress ? '#FEF3C7' : 'rgba(60,20,30,.05)', border: 'none', borderRadius: 20, padding: '3px 10px', cursor: 'pointer', fontWeight: f.in_progress ? 500 : 400 }}
                        >
                          <RotateCcw size={11} strokeWidth={1.8} />
                          {f.in_progress ? 'Avmarkera' : 'Under hantering'}
                        </button>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, color: 'var(--teal)', fontWeight: 500 }}>
                          <div style={{ width: 15, height: 15, borderRadius: 4, border: '1.5px solid var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => markResolved(f.id, true)}>
                            <Check size={10} strokeWidth={2.5} color="var(--teal)" />
                          </div>
                          Markera åtgärdad
                        </label>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {inboxTab === 'resolved' && (
              <>
                {resolvedFeedback.length === 0 && <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--muted)', fontSize: 13 }}>Inga avklarade ärenden ännu.</div>}
                {resolvedFeedback.map((f, i) => {
                  const meta = TYPE_META[f.type] || TYPE_META.feedback;
                  return (
                    <div key={f.id} style={{ padding: '12px 0', ...(i > 0 ? divider : {}), opacity: 0.7 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Check size={12} strokeWidth={2.5} color="var(--teal)" />
                          <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600 }}>{meta.label}</span>
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{f.profile?.display_name} · {new Date(f.created_at).toLocaleDateString('sv-SE')}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>{f.message}</div>
                      <button onClick={() => markResolved(f.id, false)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)', background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', marginTop: 4 }}>
                        <RotateCcw size={11} strokeWidth={1.8} />
                        Flytta tillbaka till inkorg
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* === PUSH === */}
        {tab === 'push' && (() => {
          const subscriberUsers = all.filter(u => pushSubUserIds.has(u.id));
          const allSelected = pushTargetUserIds.length === 0;
          const selectedCount = allSelected ? subscriberUsers.length : pushTargetUserIds.length;

          function toggleUser(uid) {
            if (allSelected) {
              setPushTargetUserIds(subscriberUsers.map(u => u.id).filter(id => id !== uid));
            } else {
              const next = pushTargetUserIds.includes(uid)
                ? pushTargetUserIds.filter(id => id !== uid)
                : [...pushTargetUserIds, uid];
              setPushTargetUserIds(next.length === subscriberUsers.length ? [] : next);
            }
          }

          return (
            <>
              {pushUserCount !== null && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[{ num: pushUserCount, label: 'användare' }, { num: pushSubCount, label: 'enheter' }].map(({ num, label }) => (
                    <div key={label} style={{ flex: 1, background: 'var(--cream)', border: '1px solid rgba(60,20,30,.08)', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{label}</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={sectionTitle}>Mottagare</div>
                {!allSelected && (
                  <button onClick={() => setPushTargetUserIds([])} style={{ fontSize: 11, color: 'var(--wine)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 500 }}>
                    Välj alla
                  </button>
                )}
              </div>

              {subscriberUsers.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>Inga användare har aktiverat push-notiser.</div>
              )}
              <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
                {subscriberUsers.map((u, i) => {
                  const checked = allSelected || pushTargetUserIds.includes(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', ...(i > 0 ? divider : {}), cursor: 'pointer' }}>
                      <div
                        style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${checked ? 'var(--wine)' : 'rgba(60,20,30,.2)'}`, background: checked ? 'var(--wine)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        onClick={() => toggleUser(u.id)}
                      >
                        {checked && <Check size={10} strokeWidth={2.5} color="#fff" />}
                      </div>
                      <span style={{ fontSize: 14 }}>{u.display_name}</span>
                    </label>
                  );
                })}
              </div>
              {subscriberUsers.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
                  {selectedCount} av {subscriberUsers.length} valda
                </div>
              )}

              <div style={sectionTitle}>Meddelande</div>
              <div className="field" style={{ marginTop: 8 }}>
                <label>Titel</label>
                <input value={pushTitle} onChange={e => setPushTitle(e.target.value)} placeholder="t.ex. Nyhet från Vinligan" />
              </div>
              <div className="field">
                <label>Meddelande</label>
                <textarea value={pushBody} onChange={e => setPushBody(e.target.value)} placeholder="Skriv ditt meddelande här..." rows={3} />
              </div>
              {pushResult && <div style={{ fontSize: 13, color: 'var(--teal)', marginBottom: 10, fontWeight: 500 }}>{pushResult}</div>}
              <button
                onClick={sendPush}
                disabled={sendingPush || !pushTitle.trim() || !pushBody.trim() || selectedCount === 0}
                style={{ width: '100%', background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: '11px', fontSize: 14, fontWeight: 500, cursor: 'pointer', opacity: (sendingPush || !pushTitle.trim() || !pushBody.trim() || selectedCount === 0) ? 0.6 : 1, fontFamily: 'inherit' }}
              >
                {sendingPush ? 'Skickar...' : allSelected ? `Skicka till alla (${selectedCount})` : `Skicka till ${selectedCount} valda`}
              </button>
            </>
          );
        })()}

        {/* === BADGES === */}
        {tab === 'badges' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={sectionTitle}>Alla badges ({badges.length})</div>
              <button onClick={() => setShowCreateBadge(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--wine)', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                <Plus size={13} strokeWidth={2} />
                Skapa ny
              </button>
            </div>

            {['auto', 'manual'].map(group => {
              const list = badges.filter(b => group === 'auto' ? b.is_auto : !b.is_auto);
              if (list.length === 0) return null;
              return (
                <div key={group}>
                  <div style={{ ...sectionTitle, marginTop: group === 'manual' ? 16 : 0 }}>
                    {group === 'auto' ? 'Automatiska' : 'Admin-delade'}
                  </div>
                  <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 12, overflow: 'hidden' }}>
                    {list.map((badge, i) => (
                      <div key={badge.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', ...(i > 0 ? divider : {}) }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '1px solid rgba(139,42,62,.12)' }}>
                          {badge.image_url
                            ? <img src={badge.image_url} alt={badge.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Award size={18} color="var(--wine)" strokeWidth={1.5} />
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{badge.name}</div>
                          {badge.description && <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>{badge.description}</div>}
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                            {badge.user_badges.length > 0 ? `${badge.user_badges.length} tilldelade` : 'Ingen har fått ännu'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                          <button onClick={() => { setAwardingBadge(badge); setAwardToUsers([]); setAwardResult(null); }} style={{ fontSize: 11, background: 'var(--wine-light)', color: 'var(--wine)', border: 'none', borderRadius: 20, padding: '4px 11px', cursor: 'pointer', fontWeight: 500 }}>Dela ut</button>
                          <button onClick={() => setEditingBadgeImage(badge)} style={{ fontSize: 11, background: 'rgba(60,20,30,.05)', color: 'var(--muted)', border: 'none', borderRadius: 20, padding: '4px 11px', cursor: 'pointer' }}>{badge.image_url ? 'Byt bild' : '+ Bild'}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* === POÄNG === */}
        {tab === 'points' && (
          <>
            <div style={sectionTitle}>Manuell poängjustering</div>
            <div className="field">
              <label>Medlem</label>
              <select value={pointsUserId} onChange={e => { setPointsUserId(e.target.value); setPointsResult(null); }}>
                <option value="">Välj medlem...</option>
                {all.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Aktivitet</label>
              <select value={pointsActivity} onChange={e => { setPointsActivity(e.target.value); setPointsResult(null); }}>
                {Object.keys(POINTS).map(key => (
                  <option key={key} value={key}>{ACTIVITY_LABELS[key] || key}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Referens-ID</label>
              <input value={pointsReferenceId} onChange={e => setPointsReferenceId(e.target.value)} placeholder="t.ex. manual-2026-08-11" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Poäng</label>
                <input type="number" min="1" value={pointsAmount} onChange={e => setPointsAmount(e.target.value)} placeholder="t.ex. 10" />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Typ</label>
                <select value={pointsDirection} onChange={e => setPointsDirection(e.target.value)}>
                  <option value="add">Tillägg (+)</option>
                  <option value="remove">Borttagning (-)</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }} />

            {pointsResult && (
              <div style={{ fontSize: 13, marginBottom: 12, fontWeight: 500, color: pointsResult.error ? '#a32d2d' : 'var(--teal)' }}>
                {pointsResult.message}
              </div>
            )}

            <button
              onClick={adjustPoints}
              disabled={adjustingPoints || !pointsUserId || !pointsActivity || !pointsReferenceId.trim() || !Number(pointsAmount)}
              style={{ width: '100%', background: 'var(--wine)', color: '#fff', border: 'none', borderRadius: 12, padding: '11px', fontSize: 14, fontWeight: 500, cursor: 'pointer', opacity: (adjustingPoints || !pointsUserId || !pointsActivity || !pointsReferenceId.trim() || !Number(pointsAmount)) ? 0.6 : 1, fontFamily: 'inherit' }}
            >
              {adjustingPoints ? 'Justerar...' : 'Justera poäng'}
            </button>
          </>
        )}

        <button onClick={onClose} style={{ width: '100%', marginTop: 20, background: '#fff', color: 'var(--ink)', border: '1px solid rgba(60,20,30,.12)', borderRadius: 12, padding: '11px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
          Stäng
        </button>
      </div>

      {/* Modal: skapa badge */}
      {showCreateBadge && (
        <div className="modal-bg" onClick={e => e.target === e.currentTarget && !savingBadge && setShowCreateBadge(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">Skapa ny badge</div>
            <div className="field"><label>Namn</label><input value={newBadgeName} onChange={e => setNewBadgeName(e.target.value)} placeholder="t.ex. Superprovaren" /></div>
            <div className="field"><label>Beskrivning</label><input value={newBadgeDesc} onChange={e => setNewBadgeDesc(e.target.value)} placeholder="t.ex. Betygsatt 100 viner" /></div>
            <div className="field"><label>Bild (valfritt)</label><ImageUpload currentUrl={newBadgeImageUrl} onUploaded={url => setNewBadgeImageUrl(url)} size={64} shape="circle" label="Ladda upp bild" /></div>
            <div className="btn-row">
              <button className="btn-secondary" onClick={() => setShowCreateBadge(false)}>Avbryt</button>
              <button className="btn-primary" onClick={createBadge} disabled={savingBadge || !newBadgeName.trim()}>{savingBadge ? 'Skapar...' : 'Skapa'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: dela ut badge */}
      {awardingBadge && (
        <div className="modal-bg" onClick={e => e.target === e.currentTarget && !sendingAward && (setAwardingBadge(null), setAwardResult(null))}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">Dela ut badge</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, border: '1px solid rgba(139,42,62,.12)' }}>
                {awardingBadge.image_url
                  ? <img src={awardingBadge.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <Award size={18} color="var(--wine)" strokeWidth={1.5} />
                }
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{awardingBadge.name}</div>
                {awardingBadge.description && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{awardingBadge.description}</div>}
              </div>
            </div>
            <div style={sectionTitle}>Välj medlemmar</div>
            <div style={{ background: '#fff', border: '1px solid rgba(60,20,30,.08)', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
              {all.filter(u => u.approved).map((u, i) => {
                const alreadyHas = awardingBadge.user_badges.some(ub => ub.user_id === u.id);
                return (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', ...(i > 0 ? divider : {}), cursor: alreadyHas ? 'default' : 'pointer', opacity: alreadyHas ? 0.5 : 1 }}>
                    <div
                      style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${(alreadyHas || awardToUsers.includes(u.id)) ? 'var(--wine)' : 'rgba(60,20,30,.2)'}`, background: (alreadyHas || awardToUsers.includes(u.id)) ? 'var(--wine)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      onClick={() => { if (alreadyHas) return; awardToUsers.includes(u.id) ? setAwardToUsers(p => p.filter(id => id !== u.id)) : setAwardToUsers(p => [...p, u.id]); }}
                    >
                      {(alreadyHas || awardToUsers.includes(u.id)) && <Check size={10} strokeWidth={2.5} color="#fff" />}
                    </div>
                    <span style={{ fontSize: 14, flex: 1 }}>{u.display_name}</span>
                    {alreadyHas && <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 500 }}>Har redan</span>}
                  </label>
                );
              })}
            </div>
            {awardResult && <div style={{ fontSize: 13, color: 'var(--teal)', marginBottom: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}><Check size={13} strokeWidth={2} /> {awardResult}</div>}
            <div className="btn-row">
              <button className="btn-secondary" onClick={() => { setAwardingBadge(null); setAwardResult(null); }}>Stäng</button>
              <button className="btn-primary" onClick={awardBadge} disabled={sendingAward || awardToUsers.length === 0}>
                {sendingAward ? 'Delar ut...' : `Dela ut (${awardToUsers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: badge-bild */}
      {editingBadgeImage && (
        <div className="modal-bg" onClick={e => e.target === e.currentTarget && setEditingBadgeImage(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editingBadgeImage.image_url ? 'Byt bild' : 'Lägg till bild'}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>{editingBadgeImage.name}</div>
            <ImageUpload currentUrl={editingBadgeImage.image_url} onUploaded={url => updateBadgeImage(editingBadgeImage.id, url)} size={80} shape="circle" label="Ladda upp bild" />
            <button className="btn-secondary" style={{ marginTop: 16 }} onClick={() => setEditingBadgeImage(null)}>Stäng</button>
          </div>
        </div>
      )}
    </div>
  );
}
